import { Controller } from "@hotwired/stimulus";
import {
  TOOLBAR_ITEMS,
  applyHeadingItem,
  loadTiptapModules,
  buildTiptapExtensions,
  createTiptapEditor,
  execTiptapCommand,
  isTiptapItemActive,
  handleLinkPrompt,
  handleImagePrompt,
  groupItemsForLayout,
} from "../tiptap/shared";

// Editor TipTap flotante para el iframe preview del editor visual de páginas
// (Klaviyo-style): el editor se monta sobre el `[data-field="body"]` y el
// toolbar flota arriba del bloque, anclado al `page_item_X`.
//
// **Paridad con sidebar**: la lista de items la pasa el caller via el value
// `items` — el partial `_editor_selection.html.erb` la setea con la misma
// constante Ruby (`Manage::Admin::Pages::TiptapConfig::ITEM_BODY_ITEMS`)
// que usa el sidebar form. Asi ambos toolbars tienen exactamente los
// mismos botones en el mismo orden.
//
// La unica diferencia con el sidebar es el CONTENEDOR:
//   - sidebar : clases `tiptap-toolbar-btn` etc., toolbar fila inline sin separadores
//   - inline  : clases `inline-tt-btn` etc., toolbar flotante con separadores entre grupos
//
// Lifecycle:
//   1) inline JS setea `data-controller=` + `items-value`
//   2) connect: monta editor, construye toolbar agrupado desde items
//   3) usuario edita → htmlValue se actualiza en cada cambio
//   4) inline JS lee `controller.html` al salir y guarda via fetch
//   5) inline JS remueve `data-controller=` → disconnect destruye editor

export default class extends Controller {
  static values = {
    html: { type: String, default: "" },
    items: { type: Array, default: [] },
    uploadUrl: { type: String, default: "/image/create" },
    saveUrl: { type: String, default: "" },
    saveDebounce: { type: Number, default: 400 },
  };

  async connect() {
    this._destroyed = false;
    this._saveTimer = null;
    this._lastSavedHtml = null;

    // El element del controller es el [data-field="body"]. Su contenido inicial
    // es el HTML que se va a editar.
    this._originalHtml = this.element.innerHTML;
    this._lastSavedHtml = this._originalHtml;

    // Mount del editor (ProseMirror root)
    this._editorMount = document.createElement("div");
    this._editorMount.className = "inline-tt-content text-templates";
    this.element.innerHTML = "";
    this.element.appendChild(this._editorMount);

    // Toolbar flotante — anclado al item (parent del body) para flotar arriba.
    // mousedown.preventDefault para que el click no saque el foco del editor.
    this._toolbarEl = document.createElement("div");
    this._toolbarEl.className = "inline-tt-toolbar";
    this._toolbarEl.addEventListener("mousedown", (e) => e.preventDefault());
    const itemEl =
      this.element.closest('[id^="page_item_"]') || this.element;
    itemEl.appendChild(this._toolbarEl);

    const modules = await loadTiptapModules({ withMediaExtensions: true });
    if (this._destroyed) return;

    const extensions = buildTiptapExtensions({
      modules,
      items: this.itemsValue,
      placeholder: "Escribe...",
    });

    this._editor = createTiptapEditor({
      modules,
      element: this._editorMount,
      content: this.htmlValue || this._originalHtml || "",
      extensions,
      editorProps: { attributes: { class: "inline-tt-prose" } },
      onUpdate: ({ editor }) => {
        this.htmlValue = editor.getHTML();
        this._scheduleSave();
      },
    });

    this._buildToolbar();
    this._editor.on("selectionUpdate", () => this._updateActive());
    this._editor.on("transaction", () => this._updateActive());

    this._editor.commands.focus("end");

    this.dispatch("ready");
  }

  disconnect() {
    this._destroyed = true;
    // Flush cualquier save pendiente antes de destruir el editor — asi al
    // salir del modo edit (Esc, click afuera, click en otro item) el ultimo
    // estado del HTML llega al backend.
    this._flushSave();
    if (this._editor) {
      try {
        this._editor.destroy();
      } catch (_) {}
      this._editor = null;
    }
    if (this._toolbarEl?.parentNode) this._toolbarEl.remove();
    this._toolbarEl = null;
    this._editorMount = null;
  }

  // --- Auto-save ---

  // Programa un save debounced (default 400ms — mismo timing que el sidebar
  // form auto-submit). Reemplaza cualquier save anterior pendiente.
  _scheduleSave() {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(
      () => this._saveNow(),
      this.saveDebounceValue,
    );
  }

  // Cancela el timer y ejecuta save inmediato si hay cambios pendientes.
  // Usado en disconnect para no perder el ultimo cambio del usuario.
  _flushSave() {
    if (this._saveTimer) {
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
    }
    const html = this._editor ? this._editor.getHTML() : this.htmlValue;
    if (html !== this._lastSavedHtml) this._saveNow();
  }

  // Persiste el HTML actual al endpoint section_item_bodies#update.
  // En exito notifica al parent (postMessage RECORD_SAVED) para que
  // manage--editor-draft-store marque dirty → activa "Guardar borrador".
  async _saveNow() {
    if (this._saveTimer) {
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
    }
    if (!this.saveUrlValue) return;
    const html = this._editor ? this._editor.getHTML() : this.htmlValue;
    if (html === this._lastSavedHtml) return;

    // Optimistic: marcamos como guardado antes del fetch para evitar saves
    // duplicados durante una rafaga rapida de updates. Si el fetch falla,
    // revertimos para reintentar en el proximo update.
    const attemptedHtml = html;
    this._lastSavedHtml = attemptedHtml;

    const formData = new FormData();
    formData.append("_method", "patch");
    formData.append("body", html);
    const csrf = document.querySelector("meta[name='csrf-token']")?.content;

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrf || "",
        },
        body: formData,
        credentials: "same-origin",
      });
      if (response.ok) {
        // Notifica al parent (manage--editor-draft-store) que persistio un
        // cambio para que se actualice el boton "Guardar borrador".
        window.parent.postMessage({ type: "RECORD_SAVED" }, "*");
      } else {
        this._lastSavedHtml = null;
      }
    } catch (_) {
      this._lastSavedHtml = null;
    }
  }

  // --- API pública para el inline JS ---

  get html() {
    return this._editor
      ? this._editor.getHTML()
      : this.htmlValue || this._originalHtml || "";
  }

  get originalHtml() {
    return this._originalHtml || "";
  }

  get toolbarEl() {
    return this._toolbarEl;
  }

  containsFocus(node) {
    return (
      this.element.contains(node) ||
      (this._toolbarEl && this._toolbarEl.contains(node))
    );
  }

  // --- Toolbar building (CSS-specific al contenedor flotante) ---

  _buildToolbar() {
    // Agrupa items en grupos lógicos (headings, format, lists, align, extras)
    // — los items en el mismo grupo van juntos, separados visualmente entre
    // grupos. La logica del agrupado vive en shared.js.
    const groups = groupItemsForLayout(this.itemsValue);

    groups.forEach((group, idx) => {
      if (idx > 0) this._toolbarEl.appendChild(this._buildSep());
      group.forEach((spec) => {
        let el;
        switch (spec.kind) {
          case "heading_dropdown":
            el = this._buildHeadingDropdown(spec.levels);
            break;
          case "color":
            // Color render = dos pickers (texto + fondo)
            this._toolbarEl.appendChild(this._buildColorPicker("text"));
            this._toolbarEl.appendChild(this._buildColorPicker("highlight"));
            return;
          default:
            el = this._buildButton(spec.item);
        }
        if (el) this._toolbarEl.appendChild(el);
      });
    });

    this._updateActive();
  }

  _buildSep() {
    const sep = document.createElement("div");
    sep.className = "inline-tt-sep";
    return sep;
  }

  _buildButton(item) {
    const def = TOOLBAR_ITEMS[item];
    if (!def) return null;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "inline-tt-btn";
    btn.title = def.tooltip;
    btn.dataset.item = item;
    btn.innerHTML = `<i class="${def.icon}"></i>`;
    btn.addEventListener("mousedown", (e) => e.preventDefault());
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._execItem(item);
    });
    return btn;
  }

  _buildHeadingDropdown(levels) {
    const wrap = document.createElement("div");
    wrap.className = "inline-tt-dropdown";

    const trigger = document.createElement("button");
    trigger.type = "button";
    trigger.className = "inline-tt-btn";
    trigger.title = "Encabezado";
    trigger.dataset.item = "heading";
    trigger.innerHTML =
      '<i class="fa-solid fa-heading"></i><i class="fa-solid fa-chevron-down inline-tt-chevron"></i>';
    trigger.addEventListener("mousedown", (e) => e.preventDefault());

    const menu = document.createElement("div");
    menu.className = "inline-tt-menu hidden";

    // El dropdown SOLO muestra los heading levels que el caller pidio
    // (paridad estricta con el sidebar, que muestra solo los `items`).
    levels.forEach((num) => {
      const mi = document.createElement("button");
      mi.type = "button";
      mi.className = "inline-tt-menu-item";
      mi.innerHTML = `<i class="fa-solid fa-h${num}"></i><span>Encabezado ${num}</span>`;
      mi.addEventListener("mousedown", (e) => e.preventDefault());
      mi.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        applyHeadingItem(this._editor, { level: num });
        menu.classList.add("hidden");
      });
      menu.appendChild(mi);
    });

    trigger.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      menu.classList.toggle("hidden");
    });

    document.addEventListener("click", (e) => {
      if (!wrap.contains(e.target)) menu.classList.add("hidden");
    });

    wrap.appendChild(trigger);
    wrap.appendChild(menu);
    return wrap;
  }

  _buildColorPicker(target) {
    const isHighlight = target === "highlight";
    const wrap = document.createElement("label");
    wrap.className = "inline-tt-color-wrap";
    wrap.title = isHighlight ? "Color de fondo" : "Color de texto";

    const swatch = document.createElement("span");
    swatch.className = "inline-tt-color-swatch";
    const icon = document.createElement("i");
    icon.className = isHighlight
      ? "fa-regular fa-fill-drip"
      : "fa-regular fa-text";
    const bar = document.createElement("span");
    bar.className = "inline-tt-color-bar";
    bar.style.background = isHighlight ? "#feec17" : "#000000";
    swatch.appendChild(icon);
    swatch.appendChild(bar);

    const input = document.createElement("input");
    input.type = "color";
    input.className = "inline-tt-color-input";
    input.value = isHighlight ? "#feec17" : "#000000";
    input.addEventListener("mousedown", (e) => e.preventDefault());
    input.addEventListener("input", (e) => {
      const color = e.target.value;
      bar.style.background = color;
      if (isHighlight) {
        this._editor.chain().focus().setHighlight({ color }).run();
      } else {
        this._editor.chain().focus().setColor(color).run();
      }
    });

    wrap.appendChild(input);
    wrap.appendChild(swatch);
    return wrap;
  }

  // --- Command dispatch ---

  // Delegado al exec compartido. Custom handlers:
  //   _handleLink  → shared.handleLinkPrompt
  //   _handleImage → shared.handleImagePrompt con spinner inline propio
  //
  // Los handlers de video-embed/button-link/embed NO se exponen aqui
  // porque tampoco se exponen al item body desde el sidebar (no estan
  // en `ITEM_BODY_ITEMS`). Si en el futuro se agregan al catalogo del
  // body, se enchufan aca delegando a los handlers del shared.
  _execItem(item) {
    execTiptapCommand({
      editor: this._editor,
      item,
      customHandlers: {
        _handleLink: (editor) => handleLinkPrompt(editor),
        _handleImage: (editor) =>
          handleImagePrompt(editor, this.uploadUrlValue, (show) =>
            this._showLoading(show),
          ),
      },
    });
  }

  _updateActive() {
    if (!this._toolbarEl) return;
    this._toolbarEl.querySelectorAll("[data-item]").forEach((btn) => {
      const item = btn.dataset.item;
      const active = isTiptapItemActive(this._editor, item);
      btn.classList.toggle("inline-tt-btn--active", active);
    });
  }

  // Spinner minimo (overlay sobre el bloque) mientras se sube la imagen.
  _showLoading(show) {
    const existing = this.element.querySelector(".inline-tt-loading");
    if (show && !existing) {
      const overlay = document.createElement("div");
      overlay.className = "inline-tt-loading";
      overlay.innerHTML =
        '<i class="fa-solid fa-spinner fa-spin"></i> <span>Subiendo imagen...</span>';
      this.element.appendChild(overlay);
    } else if (!show && existing) {
      existing.remove();
    }
  }
}
